import { match } from 'css-mediaquery';
import {
  type MutableRefObject,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';

import { _isBrowser } from '@dynatrace/strato-components/core';

import { useContainerSize, type ContainerSize } from './useContainerSize.js';

const getFallbackValues = (queries: string | string[]) =>
  Array.isArray(queries) ? Array<false>(queries.length).fill(false) : false;

/** Match media queries on a container */
function matchMediaContainer(
  containerSize: ContainerSize,
  queries: string,
): boolean {
  const { width, height } = containerSize;

  return (
    // currently, we only consider width and height-related media queries (not orientation queries like orientation, prefers-color-scheme etc.)
    match(queries, {
      width,
      height,
    }) ?? false
  );
}

/**
 * `useContainerBreakpoint` is a hook used to determine if the document matches a
 * media query string (or media query list) by comparing it to a container. It will
 * listen to the breakpoints specified and will return a boolean value indicating
 * if this media query is met or not. It internally relies on the useContainerSize
 * hook to determine the width and height of the container based on its ref.
 * @public
 */
export function useContainerBreakpoint(
  /** Media query you want to test, e.g. '(min-width: 800px)'. */
  query: string,
  /** Reference of the container to observe. */
  ref: MutableRefObject<HTMLElement | null>,
  /**
   * Default value (fallback value to support SSR).
   * @defaultValue false
   */
  defaultValue?: boolean,
): boolean;

/**
 * `useContainerBreakpoint` is a hook used to determine if the document matches a
 * media query string (or media query list) by comparing it to a container. It will
 * listen to the breakpoints specified and will return a boolean value indicating
 * if this media query is met or not. It internally relies on the useContainerSize
 * hook to determine the width and height of the container based on its ref.
 * @public
 */
export function useContainerBreakpoint(
  /** Media queries you want to test, e.g. '[(min-width: 400px), (min-width: 800px)]'. */
  queries: string[],
  /** Reference of the container to observe. */
  ref: MutableRefObject<HTMLElement | null>,
  /**
   * Default value (fallback value to support SSR).
   * @defaultValue [false]
   */
  defaultValues?: boolean[],
): boolean[];

/**
 * useContainerBreakpoint hook implementation
 * @public
 */
export function useContainerBreakpoint(
  /** Media query or queries you want to test, e.g. '(min-width: 800px)' or '[(min-width: 400px), (min-width: 800px)]' */
  queries: string | string[],
  /** Reference of the container to observe. */
  ref: MutableRefObject<HTMLElement | null>,
  /**
   * Default value (fallback value to support SSR).
   * @defaultValue false | [false]
   */
  defaultValues?: boolean | boolean[],
): boolean | boolean[] {
  const matches = useRef<boolean[]>([]);
  const containerSize = useContainerSize(ref);

  /* In SSR useLayoutEffect is not triggered,
   so as a fallback it is possible to provide defaults, which we return here.
   therefore we need to conditionally use the hooks in order to make SSR possible.
  */
  if (!_isBrowser) {
    return defaultValues ?? getFallbackValues(queries);
  }

  /** Get the matches depending on the type of queries (single or array). */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getMatches = useCallback(
    (queries: string | string[], containerSize: ContainerSize) => {
      if (Array.isArray(queries)) {
        return queries.map((q) => matchMediaContainer(containerSize, q));
      }

      return [matchMediaContainer(containerSize, queries as string)];
    },
    [],
  );

  matches.current = getMatches(queries, containerSize);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useLayoutEffect(() => {
    if (containerSize.height === 0 && containerSize.width === 0) {
      return; // skip initialization of container width and height (0 before the resize observer detects the size)
    }
    matches.current = getMatches(queries, containerSize); // updates the matches on resize
  }, [containerSize, getMatches, queries]);

  if (!matches.current.length) {
    return getFallbackValues(queries);
  }

  return !Array.isArray(queries) ? matches.current[0] : matches.current;
}
